import { Network, USDC_POLYGON } from '@spritz-finance/sdk'
import { Body, breakpoints, Dialog, Spacer } from '@spritz-finance/ui'
import { useState } from 'react'
import styled from 'styled-components'
import smartPay from '../../../../../../assets/icons/smart-pay.svg'
import { CreateSubscription } from './CreateSubscription'
import {
    CreateSubscriptionProvider,
    SubscriptionStep,
    useCreateSubscription,
} from './CreateSubscriptionProvider'
import { SubscriptionAccount } from './CreateSubscriptionButton'
import { Wallet } from '../../../../../../web3/Wallet'
import { FadePresence } from '../../../../../../components/FadePresence'
import { Column } from '../../../../../../components/Layout/Column'
import { Row } from '../../../../../../components/Layout/Row'
import { useWeb3Connection } from '../../../../../../web3/Web3Provider'
import { AllowanceProvider } from '../../../directPay/allowance/AllowanceProvider'
import { CircleLogo } from '../../../InstitutionImage'
import { SubscriptionConfirmation } from '../SubscriptionConfirmation'
import { SubscriptionData, SubscriptionForm } from '../SubscriptionForm'
import { Summary } from '../Summary'
import { SmartPayInfoDialog } from './SmartPayInfoDialog'

const ContentContainer = styled.div`
    max-height: 80vh;
    min-height: 195px;
    padding: 16px;
    padding-top: 0px;
    overflow: auto;
    overflow-x: clip;
    max-width: 670px;
    ${breakpoints.med} {
        max-width: unset;
        padding: 16px;
    }
`

const smartPayNetworks = [Network.Polygon]

interface CreateSubscriptionDialogProps {
    open: boolean
    onClose: () => void
    account: SubscriptionAccount
}

const Logo = styled.img`
    width: 24px;
`

const Disabled = () => {
    return (
        <>
            <Row align="center">
                <Wallet responsive clickable={false} />
            </Row>
            <Spacer height={24} />
            <Row align="center">
                <Body.Med align="center">
                    SMARTPay is currently only available on the Polygon network. Please switch
                    networks to proceed.
                </Body.Med>
            </Row>
        </>
    )
}

const ConfigureSubscription = ({
    toNextStep,
    account,
    initialData,
}: {
    toNextStep: (data: SubscriptionData) => void
    account: SubscriptionAccount
    initialData: SubscriptionData | null
}) => {
    return (
        <>
            <Row align="center">
                <Wallet responsive clickable={false} />
            </Row>
            <Spacer height={24} />
            <Row>
                <Spacer width={16} />
                <Body.Med size={12} secondary>
                    Pay to
                </Body.Med>
            </Row>
            <Spacer height={8} />
            <Row>
                <Spacer width={16} />
                <CircleLogo size={47} logoUrl={account.logoUrl} />
                <Spacer width={8} />
                <Column>
                    <Body.Med size={14} secondary>
                        {account.description}
                    </Body.Med>
                    <Body.Bold size={18}>{account.name}</Body.Bold>
                </Column>
            </Row>
            <Spacer height={24} />
            <SubscriptionForm onFormSubmit={toNextStep} initialData={initialData} />
        </>
    )
}

const CreateContent = ({
    account,
    onComplete,
}: {
    account: SubscriptionAccount
    onComplete: () => void
}) => {
    const { step, setStep } = useCreateSubscription()
    const { connectedNetwork } = useWeb3Connection()

    const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null)

    const enabled = smartPayNetworks.includes(connectedNetwork as Network)

    const toNextStep = (data?: SubscriptionData) => {
        if (data) setSubscriptionData(data)
        setStep((step) => step + 1)
    }

    if (!enabled) return <Disabled />

    let ComponentToRender: any
    switch (step) {
        case SubscriptionStep.Configure:
            ComponentToRender = (
                <ConfigureSubscription
                    account={account}
                    toNextStep={toNextStep}
                    initialData={subscriptionData}
                />
            )
            break
        case SubscriptionStep.Confirm:
            ComponentToRender = (
                <SubscriptionConfirmation
                    data={subscriptionData!}
                    account={account}
                    onClickBack={(data) => {
                        setSubscriptionData(data)
                        setStep(SubscriptionStep.Configure)
                    }}
                    toNextStep={toNextStep}
                />
            )
            break
        case SubscriptionStep.Create:
            ComponentToRender = (
                <CreateSubscription
                    data={subscriptionData!}
                    account={account}
                    toNextStep={toNextStep}
                />
            )
            break
        case SubscriptionStep.Summary:
            ComponentToRender = (
                <Summary data={subscriptionData!} account={account} onComplete={onComplete} />
            )
            break
    }

    return <FadePresence renderKey={step}>{ComponentToRender}</FadePresence>
}

const Content = ({ account, onClose }: { onClose: () => void; account: SubscriptionAccount }) => {
    const { setContainer } = useCreateSubscription()
    return (
        <ContentContainer ref={setContainer}>
            <CreateContent account={account} onComplete={onClose} />
        </ContentContainer>
    )
}

const DEFAULT_TOKEN = {
    address: USDC_POLYGON.address,
    decimals: 6,
    symbol: 'USDC',
    balance: 0,
    balanceRaw: '0',
    balanceUSD: 0,
    network: Network.Polygon,
    walletAddress: '0x0000000000000000000000000000000000000000',
    price: 0,
    tokenImageUrl:
        'https://storage.googleapis.com/zapper-fi-assets/tokens/polygon/0x2791bca1f2de4661ed88a30c99a7a9449aa84174.png',
}

const InlineButton = styled(Body.Med)`
    text-decoration: underline;
    &:hover {
        cursor: pointer;
    }
`

const DialogInner: React.VFC<CreateSubscriptionDialogProps> = ({ onClose, account }) => {
    const { step } = useCreateSubscription()
    const [infoDialogOpen, setInfoDialogOpen] = useState(false)
    return (
        <Dialog.Content contentPadding={false} minWidth={560}>
            <Dialog.Title>
                <Column style={{ margin: '0 16px' }} align="center">
                    <Row align="center">
                        <Logo src={smartPay} />
                        <Spacer width={8} />
                        <>Set up SMARTPay</>
                    </Row>
                    {step === SubscriptionStep.Configure && (
                        <>
                            <Spacer height={8} />
                            <Row style={{ maxWidth: 550 }}>
                                <Body.Med size={16} align="center">
                                    Use SMARTPay to make automatic blockchain payments to your bill
                                    each month.{' '}
                                    <InlineButton onClick={() => setInfoDialogOpen(true)} size={16}>
                                        Learn more.
                                    </InlineButton>
                                </Body.Med>
                            </Row>
                        </>
                    )}
                </Column>
            </Dialog.Title>
            <Spacer height={24} />
            <Content account={account} onClose={onClose} />
            <SmartPayInfoDialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} />
        </Dialog.Content>
    )
}

export const CreateSubscriptionDialog: React.VFC<CreateSubscriptionDialogProps> = (props) => {
    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <CreateSubscriptionProvider token={DEFAULT_TOKEN}>
                {props.open && (
                    <AllowanceProvider
                        token={{
                            address: DEFAULT_TOKEN.address,
                            symbol: DEFAULT_TOKEN.symbol,
                            network: DEFAULT_TOKEN.network,
                        }}
                    >
                        <DialogInner {...props} />
                    </AllowanceProvider>
                )}
            </CreateSubscriptionProvider>
        </Dialog>
    )
}
